import * as React from 'react'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { Well, OrgAccessControl, IOrgAccessRoleOption } from '@pushly/aqe/lib/components'
import { OrgAccessRoleIds } from '@pushly/models/lib/enums/rbac'
import { AccountService } from '@halo/services/account.service'
import { CompoundTable } from '@halo/components/compound-table/compound-table'
import { GlobalOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Divider, Flex, Modal, Radio, Segmented, Select, Skeleton, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { UserDto } from '@halo/dtos/user.dto'
import { UserService } from '@halo/services/user.service'
import UserRoleService from '@halo/services/user-role.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { AccessResetFailure, IRemovedAccountAccess } from '@halo/services/permissions.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainDto } from '@halo/dtos/domain.dto'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { IUserPermissionsResponse } from '@halo/interfaces/user-permissions'
import {
    AccessRoleId,
    ExternalAccessRoleIds,
    ExternalApiAccessRoleIds,
    InternalAccessRoleIds,
} from '@halo/enums/access-role.enum'
import AccountUserModel, { ICachedAccountUserRecord } from '@halo/models/access-control/account-user.model'
import DomainUserModel, { ICachedDomainUserRecord } from '@halo/models/access-control/domain-user.model'
import AccessControlService, { noAccessUserRoles } from '@halo/services/access-control.service'
import { AccessRoleMap } from '@halo/models/access-role'
import { observer } from 'mobx-react'
import { AppState } from '@halo/stores/app.state'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'

const apiRoleIds = [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]

/**
 * Dropdown value standing for "no row, take the platform role". It is never a role id and is
 * never written; choosing it deletes the row.
 */
const USE_ROLE_DEFAULT = -1
const NETWORK_SCOPE_UNKNOWN_MESSAGE =
    'Could not check whether this user has a platform-wide role. Rows without access may be shown as No Access.'

const resetFailureMessages: Record<AccessResetFailure, string> = {
    not_network_scope:
        "This user's platform role does not reach the whole network, so there is no role default to return to.",
    refused: 'The access row could not be reset. It may already be gone, or you may not have permission to change it.',
    failed: 'The access row could not be reset. Try again.',
}

const roleLabel = (roleId: number): string => AccessRoleMap.get(roleId)?.label ?? `Role ${roleId}`

/**
 * Derives the User_Access_Well title. When a user name is present, the title is
 * personalized; otherwise it falls back to a generic label so the Add User flow
 * (which has no user yet) never renders "Access for undefined".
 */
export function buildUserAccessWellTitle(name: string | null | undefined): string {
    const trimmed = name?.trim()
    return trimmed ? `Access for ${trimmed}` : 'User Access'
}

interface IUserAccountAccessComboDto extends AccountDto {
    permissions: {
        account?: ICachedAccountUserRecord | Omit<ICachedAccountUserRecord, 'id' | 'role_name'>
        domains: Array<ICachedDomainUserRecord | Omit<ICachedDomainUserRecord, 'id' | 'role_name'>>
    }
}

interface IUserAccountAccessView {
    user: UserDto & any
    /**
     * Whether the user's platform role reaches every organization and domain. Only then does a
     * missing row mean "the platform role applies", and only then can a row be reset.
     */
    networkScope?: boolean
    userAcs: AccessControlService
    account: IUserAccountAccessComboDto
    onChange?: (acs: AccessControlService) => any
    mode?: 'default' | 'stand-alone' | 'nested'
}

interface IUserAccountAccessViewState {
    domains: DomainDto[]
    loadingDomains: boolean
    selectedAccess: number | undefined
    domainFilter: 'all' | 'production'
}

@observer
export class UserAccountAccessView extends React.Component<IUserAccountAccessView, IUserAccountAccessViewState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService

    public constructor(props: IUserAccountAccessView) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)

        this.state = {
            loadingDomains: true,
            domains: [],
            selectedAccess: undefined,
            domainFilter: 'production',
        }
    }

    public componentDidMount(): void {
        this.fetchDomains()
    }

    public render() {
        // Access hasChanges so MobX re-renders when dirty records change
        const _hasChanges = this.props.userAcs.hasChanges

        if (this.props.mode === 'nested') {
            return this.renderNestedMode()
        }
        return this.renderDefaultMode()
    }

    protected renderNestedMode() {
        // Access hasChanges so MobX re-renders this component when bulk apply mutates dirty records
        const _hasChanges = this.props.userAcs.hasChanges

        return (
            <Flex vertical gap={8} className={classnames('user-account-access-nested')}>
                {this.renderDomainToolbar()}
                {this.renderDomainTable()}
            </Flex>
        )
    }

    /**
     * Halo grants organization access to internal users as well, so the role list
     * here is wider than the shared external list. It is passed to the control
     * rather than derived by it: the control has no way to know which surface it
     * is rendering on, and deriving it there would let a change made for one app
     * widen what the other offers.
     */
    protected get orgAccessRoleOptions(): IOrgAccessRoleOption[] {
        let roleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : OrgAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            roleIds = roleIds.filter((id) => !apiRoleIds.includes(id))
        }

        const options: IOrgAccessRoleOption[] = roleIds.map((roleId) => ({
            value: roleId,
            label: AccessRoleMap.get(roleId)?.label ?? '',
        }))

        if (this.offersRoleDefault || this.orgResetOffered) {
            const inherited = this.currentOrgRoleId === USE_ROLE_DEFAULT
            options.push({
                value: USE_ROLE_DEFAULT as AccessRoleId,
                label: inherited
                    ? `Default (inherited): ${roleLabel(this.props.user.user_role_id)}`
                    : 'Use role default',
            })
        }

        return options
    }

    protected get hasCascadingRole(): boolean {
        return (this.props.user?.isApiUser && this.props.user?.isInternalUser) || !!this.props.user?.isInternalDevUser
    }

    /**
     * For a user whose platform role reaches the network, a missing row is not "No Access":
     * the platform role applies. Cascading roles already read their role from the user.
     */
    protected get offersRoleDefault(): boolean {
        return this.props.networkScope === true && !this.hasCascadingRole
    }

    /**
     * Roles that take their access from the user record are not editable here.
     */
    protected get rolesAreLocked(): boolean {
        return !!(this.props.user?.isApiUser || this.props.user?.isInternalDevUser)
    }

    /**
     * A cascading-role user reads their role from the user, so the grid never shows a missing row
     * as a default. A stamped No Access row on such a user is still removable when the platform
     * role reaches the network, which the server checks before it deletes. An unknown scope offers
     * nothing.
     */
    protected get cascadingResetPossible(): boolean {
        return this.props.networkScope === true && this.hasCascadingRole
    }

    protected get orgResetOffered(): boolean {
        if (!this.cascadingResetPossible) {
            return false
        }

        const orgRecord = this.props.userAcs.getAccountRecord(this.props.account.id, true)
        return !!orgRecord && noAccessUserRoles.includes(orgRecord.getRoleId())
    }

    protected domainResetOffered(domain: DomainDto): boolean {
        if (!this.cascadingResetPossible) {
            return false
        }

        const domainRecord = this.props.userAcs.getDomainRecord(domain.id, true)
        return !!domainRecord && noAccessUserRoles.includes(domainRecord.getRoleId())
    }

    protected effectiveDomainRoleId(domain: DomainDto): number {
        const domainRecord = this.props.userAcs.getDomainRecord(domain.id)
        const orgRecord = this.props.userAcs.getAccountRecord(domain.account_id)

        if (this.offersRoleDefault && orgRecord && noAccessUserRoles.includes(orgRecord.getRoleId())) {
            return this.props.userAcs.noAccessRoleId
        }

        if (domainRecord) {
            return domainRecord.getRoleId()
        }

        return this.offersRoleDefault ? USE_ROLE_DEFAULT : this.props.userAcs.noAccessRoleId
    }

    /**
     * Cascading roles -- internal API users and internal dev users -- take their
     * role from the user record rather than the account_user row, because the row
     * does not govern them.
     */
    protected get currentOrgRoleId(): number {
        if ((this.props.user?.isApiUser && this.props.user?.isInternalUser) || this.props.user?.isInternalDevUser) {
            return this.props.user.user_role_id
        }

        const orgRecord = this.props.userAcs.getAccountRecord(this.props.account.id)
        if (orgRecord) {
            return orgRecord.getRoleId()
        }

        return this.offersRoleDefault ? USE_ROLE_DEFAULT : this.props.userAcs.noAccessRoleId
    }

    protected renderDefaultMode() {
        return (
            <div>
                <OrgAccessControl
                    options={this.orgAccessRoleOptions}
                    value={this.currentOrgRoleId}
                    onChange={this.updateAccountRole}
                    // Only the default layout renders this section -- nested mode routes to
                    // renderNestedMode, which shows the domain table alone.
                    variant="radio"
                    disabled={this.rolesAreLocked && !this.orgResetOffered}
                    showDisclosure={!this.props.user?.isInternalUser}
                />
                <Divider titlePlacement="start">Domain Access</Divider>
                {this.renderDomainToolbar({ style: { padding: '0 12px 8px' } })}
                <CompoundTable>{this.renderDomainTable()}</CompoundTable>
            </div>
        )
    }

    protected renderDomainToolbar(opts?: { style?: React.CSSProperties }) {
        return (
            <Flex justify="space-between" align="center" style={opts?.style}>
                <Segmented
                    className={classnames('domain-filter-toggle')}
                    value={this.state.domainFilter}
                    options={[
                        { label: 'Production Only', value: 'production' },
                        { label: 'All Domains', value: 'all' },
                    ]}
                    onChange={(value) => this.setState({ domainFilter: value as 'all' | 'production' })}
                />
                {this.selectAccessLevel()}
            </Flex>
        )
    }

    protected renderDomainTable() {
        return (
            <Table
                size="small"
                rowKey={(domain: any) =>
                    `${domain.id}-${this.props.userAcs.getDomainRecord(domain.id)?.getRoleId() ?? 'none'}`
                }
                className={classnames('user-domain-access')}
                dataSource={this.getFilteredDomains()}
                loading={
                    this.isLoading === true && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Domains" icon={<GlobalOutlined />} />,
                }}
                pagination={false}
            >
                <Table.Column key="name" className="name" title="Domain Name" render={this.renderDomain} />
                <Table.Column
                    key="access"
                    className="access"
                    title="Access Level"
                    width={180}
                    render={this.renderAccessPanel}
                />
            </Table>
        )
    }

    protected renderDomain = (_: any, domain: DomainDto) => {
        return <TableRowEntityDisplay title={domain.display_name} status={domain.name} />
    }

    protected selectAccessLevel = () => {
        let availableRoleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            availableRoleIds = availableRoleIds.filter((id) => !apiRoleIds.includes(id))
        }

        const handleChange =
            this.props.mode === 'nested'
                ? (roleId: number) => this.updateAccountDomainRolesFromSelect(roleId)
                : this.renderConfirmModal

        return (
            <Select
                disabled={this.props.user?.isApiUser || this.props.user?.isInternalDevUser}
                onChange={handleChange}
                style={{ width: 200 }}
                placeholder="Bulk Apply Access Level"
                value={null}
            >
                {availableRoleIds.map((id) => (
                    <Select.Option key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Select.Option>
                ))}
            </Select>
        )
    }

    protected renderConfirmModal = (roleId: number) => {
        const roleName = AccessRoleMap.get(roleId)?.label
        const confirm = async () => {
            await this.updateAccountDomainRolesFromSelect(roleId)
        }
        return Modal.confirm({
            okText: 'Yes, Apply',
            cancelText: 'Cancel',
            title: 'Bulk Domain Access Assignment',
            content: (
                <div>
                    <p>
                        Are you sure you want to apply <b>{roleName.toLowerCase()}</b> access to all domains?
                    </p>
                    <p>This action cannot be undone.</p>
                </div>
            ),
            onOk() {
                return confirm()
            },
        })
    }

    protected renderAccessPanel = (_: any, domain: DomainDto) => {
        const modeIsDefault = this.props.mode !== 'nested'

        let roleId = this.effectiveDomainRoleId(domain)
        if ((this.props.user?.isApiUser && this.props.user?.isInternalUser) || this.props.user?.isInternalDevUser) {
            roleId = this.props.user.user_role_id
        }

        let availableRoleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            availableRoleIds = availableRoleIds.filter((id) => !apiRoleIds.includes(id))
        }

        const roleDefaultLabel = roleId === USE_ROLE_DEFAULT ? 'Default (inherited)' : 'Use role default'
        const offersDefault = this.offersRoleDefault || this.domainResetOffered(domain)
        // A locked role keeps its roles read-only; only the reset stays live.
        const rolesDisabled = this.rolesAreLocked

        return modeIsDefault ? (
            <Radio.Group
                value={roleId}
                size="small"
                onChange={(ev) => this.handleDomainRoleChange(domain, ev.target.value)}
                // disable roles assignments for cascading roles
                disabled={rolesDisabled && !offersDefault}
            >
                {availableRoleIds.map((id) => (
                    <Radio key={id} value={id} disabled={rolesDisabled}>
                        {AccessRoleMap.get(id)?.label}
                    </Radio>
                ))}
                {offersDefault && <Radio value={USE_ROLE_DEFAULT}>{roleDefaultLabel}</Radio>}
            </Radio.Group>
        ) : (
            <Select
                value={roleId}
                size="small"
                onChange={(value: number) => this.handleDomainRoleChange(domain, value)}
            >
                {availableRoleIds.map((id) => (
                    <Select.Option key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Select.Option>
                ))}
                {offersDefault && (
                    <Select.Option key={USE_ROLE_DEFAULT} value={USE_ROLE_DEFAULT}>
                        {roleDefaultLabel}
                    </Select.Option>
                )}
            </Select>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loadingDomains
    }

    protected getFilteredDomains(): DomainDto[] {
        let domains = [...this.state.domains]

        if (this.state.domainFilter === 'production') {
            domains = domains.filter((d) => !d.is_demo && !d.is_demo_template && !d.prod_domain_id)
        }

        return domains.sort((a, b) => (a.display_name ?? a.name).localeCompare(b.display_name ?? b.name))
    }

    protected updateAccountDomainRolesFromSelect = async (roleId: number) => {
        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const accessRole = AccessRoleMap.get(roleId)
        const domainsToApply = this.props.mode === 'nested' ? this.getFilteredDomains() : this.state.domains
        const firstDomain = domainsToApply[0] ? domainsToApply[0] : null
        const domainRecords: DomainUserModel[] = []
        if (accessRole && firstDomain) {
            let orgRecord = permissionRecords.get(firstDomain.account_id)
            if (!orgRecord) {
                orgRecord = this.newParentOrgRecord(firstDomain.account_id)
            }
            domainsToApply.forEach((domain) => {
                let domainRecord = this.props.userAcs
                    .getCachedUserRecords()
                    .get(domain.account_id)
                    ?.getDomainRecord(domain.id)
                    ?.clone()

                if (domainRecord) {
                    domainRecord.setRole(accessRole)
                } else {
                    domainRecord = new DomainUserModel({
                        account_user_id: orgRecord.getId(),
                        domain_id: domain.id,
                        role_id: accessRole.id,
                        role_name: accessRole.name,
                        flags: [],
                    })
                }
                domainRecords.push(domainRecord)
            })
            await this.props.userAcs.updateCachedRecords(orgRecord.getAccountId(), orgRecord, domainRecords)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    /**
     * The organization row a domain grant hangs from when the user has none yet. Rows are the
     * access for most users, so the parent starts at No Access. A user whose platform role
     * reaches the network gets a row that follows the platform role instead: a No Access row
     * there would deny the whole organization, domains included.
     */
    protected newParentOrgRecord(accountId: number): AccountUserModel {
        const parentRoleId = this.offersRoleDefault ? this.props.user.user_role_id : this.props.userAcs.noAccessRoleId
        const parentRole = AccessRoleMap.get(parentRoleId)

        return new AccountUserModel({
            account_id: accountId,
            user_id: this.props.user.id,
            role_id: parentRole!.id,
            role_name: parentRole!.name,
            flags: [],
        })
    }

    protected handleDomainRoleChange = (domain: DomainDto, roleId: number) => {
        if (roleId === USE_ROLE_DEFAULT) {
            return this.resetDomainRole(domain)
        }
        if (this.rolesAreLocked) {
            return
        }

        return this.updateAccountDomainRole(domain, roleId)
    }

    protected async resetDomainRole(domain: DomainDto): Promise<void> {
        if (!this.props.userAcs.getDomainRecord(domain.id, true)) {
            return
        }

        this.finishReset(await this.props.userAcs.resetDomainAccess(domain.id))
    }

    protected resetAccountRole = async (): Promise<void> => {
        const accountId = this.props.account.id
        if (!this.props.userAcs.getAccountRecord(accountId, true)) {
            return
        }

        const preview = await this.props.userAcs.resetAccountAccess(accountId, true)
        if (!preview.ok) {
            return this.finishReset(preview)
        }
        if (!preview.removed) {
            return this.finishReset({ ok: false, failure: 'failed' })
        }

        this.renderResetConfirmModal(preview.removed)
    }

    protected renderResetConfirmModal(removed: IRemovedAccountAccess) {
        const domainName = (domainId: number) => {
            const domain = this.state.domains.find((d) => d.id === domainId)
            return domain ? domain.display_name ?? domain.name : `Domain ${domainId}`
        }

        const rows = removed.domains.map((row) => ({
            key: `d-${row.id}`,
            name: domainName(row.domain_id),
            roleId: row.role_id,
        }))
        const denies = [
            ...(noAccessUserRoles.includes(removed.account.role_id)
                ? [{ key: 'account', name: 'This organization', roleId: removed.account.role_id }]
                : []),
            ...rows.filter((row) => noAccessUserRoles.includes(row.roleId)),
        ]

        const platformLabel = roleLabel(this.props.user.user_role_id)

        return Modal.confirm({
            okText: 'Yes, Use Role Default',
            cancelText: 'Cancel',
            title: 'Use Role Default for This Organization',
            content: (
                <div>
                    <p>
                        This removes the user's access row for the organization and the {rows.length} domain row(s)
                        listed below. They get the access their platform role ({platformLabel}) gives them instead.
                    </p>
                    {rows.length > 0 && (
                        <ul className="reset-domain-rows">
                            {rows.map((row) => (
                                <li key={row.key}>
                                    {row.name}: {roleLabel(row.roleId)}
                                </li>
                            ))}
                        </ul>
                    )}
                    {denies.length > 0 && (
                        <div className="reset-denies">
                            <p>
                                <b>These No Access rows are explicit denies, and removing them lifts the deny:</b>
                            </p>
                            <ul>
                                {denies.map((row) => (
                                    <li key={row.key}>{row.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            ),
            onOk: async () => {
                this.finishReset(await this.props.userAcs.resetAccountAccess(this.props.account.id))
            },
        })
    }

    protected finishReset(res: {
        ok: boolean
        failure?: AccessResetFailure
        cacheStale?: boolean
        reloaded?: boolean
    }) {
        if (!res.ok) {
            simpleNotification('error', resetFailureMessages[res.failure ?? 'failed'])
            return
        }

        if (res.cacheStale) {
            simpleNotification(
                'warning',
                'The access row was removed, but the change can take up to an hour to take effect.',
            )
        }
        if (res.reloaded === false) {
            simpleNotification(
                'warning',
                'The access row was removed, but the grid could not be refreshed. Reload the page.',
            )
        }

        this.forceUpdate()
        this.props.onChange?.(this.props.userAcs)
    }

    protected async updateAccountDomainRole(domain: DomainDto, roleId: AccessRoleId) {
        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const accessRole = AccessRoleMap.get(roleId)

        if (accessRole) {
            let orgRecord = permissionRecords.get(domain.account_id)
            if (!orgRecord) {
                orgRecord = this.newParentOrgRecord(domain.account_id)
            }

            let domainRecord = this.props.userAcs
                .getCachedUserRecords()
                .get(domain.account_id)
                ?.getDomainRecord(domain.id)
                ?.clone()

            if (domainRecord) {
                domainRecord.setRole(accessRole)
            } else {
                domainRecord = new DomainUserModel({
                    account_user_id: orgRecord.getId(),
                    domain_id: domain.id,
                    role_id: accessRole.id,
                    role_name: accessRole.name,
                    flags: [],
                })
            }

            await this.props.userAcs.updateCachedRecord(orgRecord.getAccountId(), orgRecord, domainRecord)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    protected updateAccountRole = async (roleId: number) => {
        if (roleId === USE_ROLE_DEFAULT) {
            return this.resetAccountRole()
        }
        if (this.rolesAreLocked) {
            return
        }

        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const accessRole = AccessRoleMap.get(roleId)

        if (accessRole) {
            let orgRecord = permissionRecords.get(this.props.account.id)?.clone()

            if (orgRecord) {
                orgRecord.setRole(accessRole)
            } else {
                orgRecord = new AccountUserModel({
                    account_id: this.props.account.id,
                    user_id: this.props.user.id,
                    role_id: roleId,
                    role_name: accessRole!.name,
                    flags: [],
                })
            }

            await this.props.userAcs.updateCachedRecord(orgRecord.getAccountId(), orgRecord)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    protected async fetchDomains(): Promise<void> {
        if (!this.props.account) {
            setTimeout(() => this.fetchDomains(), 50)
            return
        }

        let update: any = {
            loadingDomains: true,
            domains: [],
        }

        await this.setState(update)

        update = {
            loadingDomains: false,
        }

        const domains = await this.accountService.fetchDomains(this.props.account.id, {
            query: { pagination: false },
            showLoadingScreen: false,
            cancellationKey: `user-view-fetch-account-${this.props.account.id}-domains`,
        })

        if (domains.ok) {
            update.domains = domains.data.filter((domain) => {
                return this.appState.abilityStore.can(
                    AbilityAction.UPDATE,
                    asCaslSubject(SubjectEntity.DOMAIN, {
                        ...domain,
                        statusId: domain.status_id,
                    }),
                )
            })
        }

        await this.setState(update)
    }
}

interface IUserAccessWell {
    user?: UserDto & any
    accountId?: number
    loading?: boolean
    onChange?: (permissions: any) => any
    mode?: 'default' | 'stand-alone' | 'nested'
}

interface IUserAccessWellState {
    propsUserAcs: AccessControlService
    accounts: IUserAccountAccessComboDto[]
    loadingDependencies: boolean
    activeAccount?: IUserAccountAccessComboDto
    /** undefined until known, and left undefined when the lookup failed */
    networkScope?: boolean
}

@observer
export class UserAccessWell extends React.Component<IUserAccessWell, IUserAccessWellState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService
    protected userRoleService: UserRoleService

    public constructor(props: IUserAccessWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)
        this.userRoleService = Container.get(UserRoleService)

        this.state = {
            propsUserAcs: AccessControlService.forUser(this.user),
            loadingDependencies: false,
            accounts: [],
        }
    }

    public async componentDidMount(): Promise<void> {
        this.fetchDependencies()
    }

    public componentDidUpdate(prevProps: Readonly<IUserAccessWell>) {
        if (this.props.user && this.props.user.user_type_id !== prevProps.user?.user_type_id) {
            this.setState({ propsUserAcs: AccessControlService.forUser(this.props.user) })
        }
    }

    public render() {
        const modeIsDefault = this.props.mode !== 'nested'

        return (
            <Well
                className={classnames('user-access-well', {
                    nested: true,
                    has_changes: this.state.propsUserAcs.hasChanges,
                    ['user-access-nested-well']: !modeIsDefault,
                    ['type-2']: !modeIsDefault,
                    ['type-3']: modeIsDefault,
                })}
                title={this.wellTitle}
                hideFooter={true}
                mode={modeIsDefault ? 'default' : 'ghost'}
                loading={this.props.loading}
                actions={
                    <Select
                        className="account-select"
                        size={modeIsDefault ? 'middle' : 'small'}
                        onChange={this.handleActiveAccountSelection}
                        value={this.isLoading ? undefined : this.props.accountId || this.state.activeAccount?.id}
                        disabled={this.isLoading || !!this.props.accountId}
                        placeholder={this.isLoading ? 'Loading...' : 'Select an Org'}
                        showSearch={true}
                        filterOption={true}
                        optionFilterProp="title"
                    >
                        {!this.isLoading &&
                            this.state.accounts.map((account) => (
                                <Select.Option key={account.id} value={account.id} title={account.name}>
                                    {account.name}
                                </Select.Option>
                            ))}
                    </Select>
                }
            >
                {this.appState.currentUser?.isExternalUser && this.user.isInternalUser ? (
                    <div className="internal-user-message">Internal user access cannot be modified.</div>
                ) : (
                    <Skeleton active={true} loading={this.isLoading} title={false}>
                        {!this.isLoading &&
                            (!this.state.activeAccount ? (
                                <div className="no-account-selected-message">
                                    Please select an organization to view permissions.
                                </div>
                            ) : (
                                <UserAccountAccessView
                                    key={this.state.activeAccount!.id}
                                    mode={this.props.mode}
                                    user={this.user}
                                    networkScope={this.state.networkScope}
                                    userAcs={this.state.propsUserAcs}
                                    account={this.state.activeAccount!}
                                    onChange={(acs) => {
                                        this.props.onChange?.(acs.getDirtyRecords())
                                    }}
                                />
                            ))}
                    </Skeleton>
                )}
            </Well>
        )
    }

    protected get isLoading(): boolean {
        return this.props.loading || this.state.loadingDependencies
    }

    protected get wellTitle(): string {
        return buildUserAccessWellTitle(this.user?.name)
    }

    protected get user(): UserDto {
        return this.props.user ?? new UserDto()
    }

    protected handleActiveAccountSelection = async (accountId?: number): Promise<void> => {
        let activeAccount
        if (accountId) {
            activeAccount = this.state.accounts.find((account) => account.id === accountId)
        }

        return this.setState({ activeAccount })
    }

    protected async fetchDependencies(): Promise<void> {
        const update: Omit<IUserAccessWellState, 'propsUserAcs'> = {
            loadingDependencies: true,
            accounts: [],
        }

        await this.setState({ ...update })
        update.loadingDependencies = false

        update.networkScope = await this.fetchNetworkScope()

        const userPermissions = await this.fetchPermissions()
        const reqAccountPermissions = userPermissions.accounts.find((a) => a.id === this.props.accountId)
        const reqAccount = update.accounts.find((a: AccountDto) => a.id === reqAccountPermissions.account_id)

        const { data } = await this.accountService.fetchAll({
            query: {
                pagination: 0,
            },
            showLoadingScreen: false,
            cancellationKey: 'user-view-fetch-accounts',
        })

        update.accounts = data

        if (this.user.isExternalUser && reqAccountPermissions && reqAccount) {
            update.activeAccount = reqAccount
        }

        if (this.props.accountId) {
            update.activeAccount = update.accounts.find((a: AccountDto) => a.id === this.props.accountId)
        }

        const noAccessRoleId = this.user.isInternalUser
            ? AccessRoleId.INTERNAL_NO_ACCESS
            : AccessRoleId.EXTERNAL_NO_ACCESS

        const emptyPermissionsConfig: IUserAccountAccessComboDto['permissions'] = {
            domains: [],
        }

        for (const acct of update.accounts) {
            acct.permissions = acct.permissions ?? { ...emptyPermissionsConfig }

            const acctPermissions: ICachedAccountUserRecord = userPermissions.accounts.find(
                (perms) => perms.account_id === acct.id,
            )

            let domainPermissions: ICachedDomainUserRecord[] = []
            if (acctPermissions?.id) {
                domainPermissions = userPermissions.domains.filter((d) => d.account_user_id === acctPermissions.id)
            }

            acct.permissions.account = acctPermissions ?? {
                account_id: acct.id,
                user_id: this.user.id,
                role_id: noAccessRoleId,
                flags: [],
            }
            acct.permissions.domains = domainPermissions
        }

        await this.setState(update)
    }

    /**
     * The user record carries the platform role id but not what the role reaches, and the flag
     * is editable, so it is read rather than assumed. A failed lookup stays unknown (undefined)
     * instead of becoming false, so the grid never claims "No Access" for a user it could not
     * classify without saying so.
     *
     * The assignable list omits inactive roles, roles outside the valid internal set, and the
     * developer role for a non-developer. A role missing from it was never classified, so it is
     * unknown too. No other endpoint a non-developer can call returns the flag.
     */
    protected async fetchNetworkScope(): Promise<boolean | undefined> {
        if (!this.user.id || !this.user.isInternalUser) {
            return false
        }

        const res = await this.userRoleService.fetchAssignable()
        if (!res.ok) {
            simpleNotification('error', NETWORK_SCOPE_UNKNOWN_MESSAGE)
            return undefined
        }

        const role = res.data.find((r) => r.id === this.user.user_role_id)
        if (!role) {
            // Cascading roles never show a missing row as a default, so the warning would not apply.
            if (!this.user.isInternalDevUser && !(this.user.isApiUser && this.user.isInternalUser)) {
                simpleNotification('error', NETWORK_SCOPE_UNKNOWN_MESSAGE)
            }
            return undefined
        }

        return role.has_network_scope === true
    }

    protected async fetchPermissions(): Promise<IUserPermissionsResponse> {
        const permissions: IUserPermissionsResponse = {
            accounts: [],
            domains: [],
        }

        if (this.user.id) {
            await this.state.propsUserAcs.getUserRecords()

            const opts: any = {
                showLoadingScreen: false,
                cancellationKey: 'user-view-fetch-perms',
            }

            const {
                data: { accounts, domains },
            } = await this.userService.fetchPermissions(this.user.id, opts)

            permissions.accounts = accounts
            permissions.domains = domains
        }

        return permissions
    }
}
